import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import ContactRow from '@/components/clients/ContactRow';
import { Inbox, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const ContactsListSkeleton = () => (
  <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
    <CardContent className="p-3 sm:p-4 space-y-2.5">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-4 rounded-md border border-border bg-[hsl(var(--background))]/40 px-4 py-3.5"
        >
          <Skeleton className="h-10 w-10 rounded-full bg-[hsl(var(--muted))]" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-1/3 bg-[hsl(var(--muted))]" />
            <Skeleton className="h-3 w-2/3 bg-[hsl(var(--muted))]" />
          </div>
          <Skeleton className="h-3 w-16 bg-[hsl(var(--muted))] hidden sm:block" />
        </div>
      ))}
    </CardContent>
  </Card>
);

export const ContactsList = ({
  contacts = [],
  onOpenContact,
  emptyTitle,
  emptyDescription,
  onAddLead,
}) => {
  const { t } = useTranslation();
  const title = emptyTitle ?? t('clientsLeads.empty.noContactsTitle');
  const description = emptyDescription ?? t('clientsLeads.empty.noContactsDesc');
  if (!contacts || contacts.length === 0) {
    return (
      <Card
        className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
        data-testid="contacts-empty"
      >
        <CardContent className="py-12 px-6 text-center">
          <div className="mx-auto size-10 rounded-md bg-[hsl(var(--muted))] border border-border flex items-center justify-center text-[hsl(var(--primary))]">
            <Inbox size={20} strokeWidth={1.75} />
          </div>
          <h3 className="mt-3 text-sm sm:text-base font-semibold text-foreground">
            {title}
          </h3>
          <p className="mt-1 text-xs sm:text-sm text-muted-foreground max-w-sm mx-auto">
            {description}
          </p>
          {onAddLead ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onAddLead}
              className="mt-4 border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
              data-testid="contacts-empty-add-lead"
            >
              <UserPlus size={14} className="mr-2" />
              {t('clientsLeads.empty.addCta')}
            </Button>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
      <CardContent className="p-3 sm:p-4">
        <div className="space-y-2.5" data-testid="contacts-list">
          {contacts.map((contact) => (
            <ContactRow
              key={contact.id}
              contact={contact}
              onOpen={onOpenContact}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ContactsList;
